import React from 'react';
import { OrderControls, StatusInfoCell, KeyBadge, StatusActionButtons } from '@molecules';
import { Badge } from '@atoms';

export interface Status {
  id: string;
  key: string;
  label: string;
  description: string | null;
  color: string | null;
  orderNum: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StatusTableProps {
  statuses: Status[];
  searchTerm: string;
  onEditStatus?: (status: Status) => void;
  onDeleteStatus?: (status: Status) => void;
  onMoveUp?: (status: Status) => void;
  onMoveDown?: (status: Status) => void;
  isDeleteDisabled?: (status: Status) => boolean;
  showActions?: boolean;
}

export const StatusTable: React.FC<StatusTableProps> = ({
  statuses,
  searchTerm,
  onEditStatus,
  onDeleteStatus,
  onMoveUp,
  onMoveDown,
  isDeleteDisabled,
  showActions = true
}) => {
  // Filter statuses based on search term
  const filteredStatuses = statuses.filter(status =>
    status.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (status.description && status.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort statuses by order number
  const sortedStatuses = [...filteredStatuses].sort((a, b) => a.orderNum - b.orderNum);

  return (
    <div className="bg-background-primary rounded-lg border border-border-primary overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Active
              </th>
              {showActions && (
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {sortedStatuses.length === 0 ? (
              <tr>
                <td colSpan={showActions ? 6 : 5} className="px-6 py-8 text-center text-text-secondary">
                  {searchTerm ? 'No statuses found matching your search.' : 'No statuses found.'}
                </td>
              </tr>
            ) : (
              sortedStatuses.map((status, index) => (
                <tr key={status.id} className="hover:bg-background-secondary">
                  <td className="px-6 py-4">
                    <OrderControls
                      orderNum={status.orderNum}
                      canMoveUp={index > 0}
                      canMoveDown={index < sortedStatuses.length - 1}
                      onMoveUp={onMoveUp ? () => onMoveUp(status) : undefined}
                      onMoveDown={onMoveDown ? () => onMoveDown(status) : undefined}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <StatusInfoCell
                      label={status.label}
                      color={status.color}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <KeyBadge keyValue={status.key} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-text-secondary">
                      {status.description || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      variant={status.isActive ? 'success' : 'error'}
                      size="sm"
                    >
                      {status.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 text-right">
                      <StatusActionButtons
                        onEdit={onEditStatus ? () => onEditStatus(status) : undefined}
                        onDelete={onDeleteStatus ? () => onDeleteStatus(status) : undefined}
                        isDeleteDisabled={isDeleteDisabled?.(status) || false}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
